import React from 'react';
import { CreditCard, Shield, UserCheck, Brain, Lock } from 'lucide-react';
import useScrollFadeIn from '../hooks/useScrollFadeIn';
import { trustBadges } from '../data/stats';

const trustPoints = [
  {
    icon: CreditCard,
    title: 'No Credit Card Required',
    description: 'Start building your AI assistant completely free. No payment info needed to get started.',
    proof: 'Free during early access',
  },
  {
    icon: Shield,
    title: 'Secure by Design',
    description: 'Your data is encrypted in transit and at rest. We never sell or share your information.',
    proof: 'TLS 1.3 · GDPR aware',
  },
  {
    icon: UserCheck,
    title: 'You Stay in Control',
    description: 'Manage, pause, or update your assistant anytime. Your business, your rules.',
    proof: 'Pause or delete anytime',
  },
  {
    icon: Brain,
    title: 'AI Answers from Your Knowledge Only',
    description: 'Your assistant never makes things up. It only responds using the documents you provide.',
    proof: 'Zero hallucinations from out of scope queries',
  },
];

export default function TrustSafety() {
  const { ref, isVisible } = useScrollFadeIn();

  return (
    <section
      id="trust"
      className="py-20 sm:py-28 px-4 sm:px-6 lg:px-8 relative"
      style={{ background: 'var(--bg-section-2)' }}
    >
      <div
        ref={ref}
        className={`max-w-6xl mx-auto transition-all duration-700 ease-out ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        {/* Section heading */}
        <div className="text-center mb-16">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4" style={{ color: 'var(--text-heading)' }}>
            Built for Trust and Transparency
          </h2>
          <p className="text-base sm:text-lg max-w-2xl mx-auto" style={{ color: 'var(--text-body)' }}>
            We believe your AI assistant should be safe, reliable, and fully under your control.
          </p>
        </div>

        {/* Trust cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {trustPoints.map((point, index) => {
            const Icon = point.icon;
            return (
              <div
                key={index}
                className="rounded-2xl p-6 text-center transition-all duration-300 hover:-translate-y-1"
                style={{
                  background: 'var(--bg-card)',
                  border: '1px solid var(--border-card)',
                  boxShadow: 'var(--shadow-card)',
                  backdropFilter: 'blur(var(--glass-blur))',
                  WebkitBackdropFilter: 'blur(var(--glass-blur))',
                  transitionDelay: `${index * 80}ms`,
                }}
              >
                <div
                  className="w-14 h-14 rounded-xl flex items-center justify-center mx-auto mb-5"
                  style={{ background: 'var(--bg-icon)' }}
                >
                  <Icon className="w-7 h-7" style={{ color: 'var(--text-accent)' }} />
                </div>
                <h3 className="text-base font-semibold mb-2" style={{ color: 'var(--text-heading)' }}>
                  {point.title}
                </h3>
                <p className="text-sm leading-relaxed mb-3" style={{ color: 'var(--text-body)' }}>
                  {point.description}
                </p>
                {point.proof && (
                  <span
                    className="inline-block text-xs font-semibold px-2.5 py-1 rounded-full"
                    style={{
                      background: 'var(--bg-icon)',
                      color: 'var(--text-accent)',
                    }}
                  >
                    {point.proof}
                  </span>
                )}
              </div>
            );
          })}
        </div>

        {/* Security badges row */}
        <ul className="mt-12 flex flex-wrap items-center justify-center gap-2 sm:gap-3">
          {trustBadges.map((label) => (
            <li
              key={label}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium"
              style={{
                background: 'var(--bg-card)',
                border: '1px solid var(--border-card)',
                color: 'var(--text-body)',
              }}
            >
              <Lock className="w-3 h-3" style={{ color: 'var(--text-accent)' }} />
              {label}
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
